import {useKeycloak} from "@react-keycloak/web";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    BrowserRouter,
    Navigate,
    Route,
    Routes,
    useLocation,
    useParams,
} from "react-router-dom";
import ChSidebar from "./components/sidebar/Sidebar.tsx";
import {logo, translateSections} from "./utilis/menuItems.tsx";
import Calls from "./pages/Calls/Calls.tsx";
import ContactsList from "./pages/Contacts/Contacts.tsx";

import ContactDetails from "./pages/Contacts/ContactDetails.tsx";
import TeamList from "./pages/Team/TeamList.tsx";
import GroupList from "./pages/Groups/GroupList.tsx";
import GroupDetails from "./pages/Groups/GroupDetails.tsx";
import {useSelector} from "react-redux";
import {jwtDecode} from "jwt-decode";
import PhoneNumberSettings from "./pages/PhoneNumbers/PhoneNumberSettings";
import {PhoneNumbers} from "./pages/PhoneNumbers/PhoneNumbers";
import {sipUserAgent, stopSipForBlockedAccount} from "./components/Dialer/services/sipService";
import ToastNotification from "./components/Notifications/Notifications";
import {CallSurfaceRoot} from "./features/callSurface/CallSurfaceRoot.tsx";
import ProfileSettings from "./pages/ProfileSettings/ProfileSettings";
import {
    clearAuthData,
    initializeDeviceId,
    selectDeviceId,
    selectIsSuperadmin,
    selectLogoutInProgress,
    readPersistedMatrixSession,
    selectPrincipalTenant,
    selectPrincipalUserId,
    selectTenant,
    selectUserId,
    selectUserRole,
    setAuthContext,
    setMatrixSession,
} from "./store/features/Auth/authSlice";
import RecordingDownload from "./pages/Recordings/GetRecording";
import {useAppDispatch, RootState} from "./store/store";
import Integrations from "./pages/Integrations/Integrations";
import {Integration} from "./pages/Integrations/integration";
import {CallQueuesPage} from "./pages/CallQueues/CallQueuesPage";
import Header from "./components/header/Header";
import {BillingReport} from "./pages/Report/BIllingReport";
import CallTranscript from "./pages/Calls/CallTranscript.tsx";
import TeamListAdmin from "./pages/Team/TeamListAdmin.tsx";
import TeamDetailsAdmin from "./pages/Team/TeamDetailsAdmin.tsx";
import OnlineStatusDetector from "./components/OnlineOfflineDetector/OnlineOfflineDetector.tsx";
import { logAuthSuccess, logDev, logError } from "./utils/loggerHelpers";
import { getStoredDeviceId } from "./utils/deviceIdStorage.ts";
import { ChatOfflineIndicator } from "./components/ChatOfflineIndicator/ChatOfflineIndicator.tsx";
import {
    attachChatInitAutoRetry,
    createChatInitRetryScheduler,
    createSoftLogoutRecovery,
    type SoftLogoutRecovery,
    isAutoRetryableChatInitFailure,
    isPermanentChatInitError,
    ChatInitAttemptTracker,
    type ChatInitFailureState,
    type ChatInitRetryScheduler,
    type ChatInitRunResult,
} from "./utils/chatInitRetry.ts";
import Chat from "./pages/Chat/Chat.tsx";
import Gleap from "gleap";
import HomePage from "./pages/Home/Home.tsx";
import {SettingsLayout} from "./pages/Settings/SettingsLayout.tsx";
import {AdminSettingsFrame} from "./pages/Settings/AdminSettingsFrame.tsx";
import GeneralSettings from "./pages/Settings/GeneralSettings.tsx";
import AudioSettingsPage from "./pages/Settings/AudioSettingsPage.tsx";
import PermissionsSettings from "./pages/Settings/PermissionsSettings.tsx";
import AdminPanelRestrictedView, {
    RestrictedView
} from "./components/AdminPanelRestrictedView/AdminPanelRestrictedView.tsx";
import {
    useGetUserProfileQuery,
    useUpdateUserTimezoneMutation
} from "./store/features/ProfileSettings/profileSettingsService.ts";
import IncomingChatWebhooks from "./pages/Integrations/Webhook/IncomingChatWebhooks";
import WebhookList from "./pages/Integrations/Webhook/WebhookList.tsx";
import WebhookDeliveries from "./pages/Integrations/Webhook/WebhookDeliveries.tsx";
import ApiList from "./pages/Integrations/API/Apilist.tsx";
import EnsMain from "./pages/Ens/EnsMain.tsx";
import EnsUserContactsAdmin from "./pages/Ens/UserContacts/EnsUserContactsAdmin.tsx";
import MyAlerts from "./pages/Ens/MyAlerts/MyAlerts.tsx";
import {EnsNotificationPoller} from "./pages/Ens/Notifications/EnsNotificationPoller.tsx";
import {CallChangesPoller} from "./features/callChanges/CallChangesPoller.tsx";
import {getAccountBlocked, recordAccountBlock, useAccountBlocked} from "./services/accountAccessState.ts";
import {AccountBlockedNotice} from "./components/AccountBlockedNotice/AccountBlockedNotice.tsx";
import {useIsChatEnabledQuery, useIsEnsEnabledQuery, useLoginMutation} from "./services/chatService.ts";
import type {ChatLoginResponse} from './services/chatServiceTypes.ts';
import {useFetchCurrentUserQuery, useMarkChatInitializedMutation} from "./store/features/Users/userService.ts";
import {useMatrixContext} from "./providers/MatrixProvider.tsx";
import {StatusProvider} from "./providers/StatusProvider.tsx";
import api from "./store/api.ts";
import {subscribeToAccessTokenChanges} from "./services/authRuntime.ts";
import {SuperAdminBanner} from "./components/SuperAdminBanner/SuperAdminBanner.tsx";
import {
    acquireMatrixSessionLock,
    cancelMatrixSessionLockTakeover,
    hasPendingMatrixSessionLockClaim,
    releaseMatrixSessionLock,
    requestMatrixSessionLockTakeover,
    subscribeToMatrixSessionLock,
} from "./services/matrixSessionLock.ts";
import type {MatrixSessionLockScope} from "./services/matrixSessionLock.ts";
import {markChatLockAutoClaimSpent, shouldAutoClaimChatLock} from "./utils/chatLockAutoClaim.ts";
import {MatrixCryptoStoreEvictedError, matrixService} from "./services/matrixService.ts";
import {MatrixDeviceIdMismatchError} from './services/matrixDeviceIdRecovery.ts';
import {
    buildMatrixAccountContextKey,
    enforceMatrixAccountBoundary,
    type MatrixAccountContext,
    shouldResetMatrixForAccountChange,
} from "./services/matrixAccountBoundary.ts";

interface AuthenticatedToken {
    tenant: string;
    userId: string;
    given_name?: string;
    family_name?: string;
    resource_access?: Record<string, {roles?: string[]}>;
}

type ApplicationAuthContext = MatrixAccountContext;

type DerivedAuth =
    | {status: 'initializing'}
    | {status: 'unauthenticated'}
    | {status: 'error'; error: unknown}
    | {status: 'ready'; context: ApplicationAuthContext; contextKey: string};

const determineSuperAdminContext = (
    tokenUserId: string,
    tokenTenant: string,
    storedTenant: string | null,
    storedUserId: string | null,
) => {
    const urlTenant = new URLSearchParams(window.location.search).get('tenantId');
    const urlUserId = new URLSearchParams(window.location.search).get('userId');

    const effectiveTenant = urlTenant || storedTenant;
    const effectiveUserId = urlUserId || storedUserId;

    const context = {
        isOnClientAccount: false,
        effectiveTenant: tokenTenant,
        effectiveUserId: tokenUserId,
        role: 'SUPERADMIN',
    };

    if (effectiveTenant && effectiveTenant !== tokenTenant) {
        context.isOnClientAccount = true;
        context.effectiveTenant = effectiveTenant;
        context.effectiveUserId = effectiveUserId || tokenUserId;
        context.role = 'SUPERADMIN';
    } else if (effectiveUserId && effectiveUserId !== tokenUserId) {
        context.isOnClientAccount = true;
        context.effectiveUserId = effectiveUserId;
        context.effectiveTenant = effectiveTenant || tokenTenant;
        context.role = 'SUPERADMIN';
    }
    logDev("Super Admin Context determined", {
        source: 'auth',
        event: 'superadmin_context',
        context: JSON.stringify(context),
        urlUserId,
        urlTenant,
        storedTenant,
        storedUserId
    });
    return context;
};

function App() {

    const dispatch = useAppDispatch();
    const [authStatus, setAuthStatus] = useState<
        'initializing' | 'ready' | 'unauthenticated' | 'error'
    >('initializing');
    const [tokenRevision, setTokenRevision] = useState(0);
    const activeContextKeyRef = useRef<string | null>(null);
    const authTransitionRevisionRef = useRef(0);
    const matrixCleanupPendingRef = useRef(false);
    const matrixCleanupFailedRef = useRef(false);
    const matrixCleanupPromiseRef = useRef<Promise<void>>(Promise.resolve());

    const storedTenant = useSelector(selectTenant);
    const storedUserId = useSelector(selectUserId);
    const storedPrincipalTenant = useSelector(selectPrincipalTenant);
    const storedPrincipalUserId = useSelector(selectPrincipalUserId);
    const logoutInProgress = useSelector(selectLogoutInProgress);
    const sipInstanceId = useSelector((state: RootState) => state.dialer.sipInstanceId);

    const {keycloak, initialized} = useKeycloak();

    const resetMatrixSession = useCallback((): Promise<void> => {
        if (matrixCleanupPendingRef.current) {
            return matrixCleanupPromiseRef.current;
        }

        matrixCleanupPendingRef.current = true;
        const cleanup = matrixService.logout()
            .then(() => {
                matrixCleanupFailedRef.current = false;
            })
            .catch((error) => {
                // Keep the boundary dirty. A later account must retry cleanup
                // instead of assuming that null persisted principals mean the
                // previous Matrix state was removed successfully.
                matrixCleanupFailedRef.current = true;
                throw error;
            })
            .finally(() => {
                if (matrixCleanupPromiseRef.current === cleanup) {
                    matrixCleanupPendingRef.current = false;
                }
            });
        matrixCleanupPromiseRef.current = cleanup;
        return cleanup;
    }, []);


    useEffect(() => subscribeToAccessTokenChanges(() => {
        setTokenRevision((revision) => revision + 1);
    }), []);

    // Initialize scoped device ID (tenant_userId)
    useEffect(() => {
        dispatch(initializeDeviceId({
            tenant: storedTenant,
            userId: storedUserId,
        }));
    }, [dispatch, storedTenant, storedUserId]);

    useEffect(() => {
        if (import.meta.env.MODE == 'production' && window.__ENV__?.GLEAP_ID) {
            Gleap.setNetworkLogsBlacklist(['/api/v1/chats/webhooks', '/api/v1/chat-webhooks/incoming']);
            Gleap.initialize(window.__ENV__.GLEAP_ID);
        }
    }, []);

    // Derive the application auth context purely from the decoded token and the
    // stored impersonation inputs. Identity only changes on login or an
    // impersonation switch, so a silent token refresh that keeps the same
    // identity produces the same contextKey and triggers no extra work below.
    const derivedAuth = useMemo<DerivedAuth>(() => {
        if (!initialized) {
            return {status: 'initializing'};
        }

        // Keycloak keeps the old token until its logout redirect completes.
        // Do not let that token recreate the application/Matrix session after
        // the user has explicitly requested logout.
        if (logoutInProgress) {
            return {status: 'unauthenticated'};
        }

        if (!keycloak.authenticated || !keycloak.token) {
            return {status: 'unauthenticated'};
        }

        try {
            const decodedToken = jwtDecode<AuthenticatedToken>(keycloak.token);
            const roles: string[] = decodedToken.resource_access?.['backend-service']?.roles ?? [];
            const isSuperAdmin = roles.includes('SUPERADMIN');
            let context: ApplicationAuthContext;

            if (isSuperAdmin) {
                const canRestoreEffectiveContext =
                    storedPrincipalTenant === decodedToken.tenant &&
                    storedPrincipalUserId === decodedToken.userId;
                const superAdminContext = determineSuperAdminContext(
                    decodedToken.userId,
                    decodedToken.tenant,
                    canRestoreEffectiveContext ? storedTenant : null,
                    canRestoreEffectiveContext ? storedUserId : null,
                );

                context = {
                    principalTenant: decodedToken.tenant,
                    principalUserId: decodedToken.userId,
                    tenant: superAdminContext.effectiveTenant,
                    userId: superAdminContext.effectiveUserId,
                    role: superAdminContext.role,
                    isImpersonating: superAdminContext.isOnClientAccount,
                    isSuperadmin: true,
                };
            } else {
                context = {
                    principalTenant: decodedToken.tenant || '',
                    principalUserId: decodedToken.userId || '',
                    tenant: decodedToken.tenant || '',
                    userId: decodedToken.userId || '',
                    role: roles.includes('ADMIN') ? 'ADMIN' : 'USER',
                    isImpersonating: false,
                    isSuperadmin: false,
                };
            }

            const contextKey = buildMatrixAccountContextKey(context);

            return {status: 'ready', context, contextKey};
        } catch (error) {
            return {status: 'error', error};
        }
    }, [
        initialized,
        logoutInProgress,
        keycloak.authenticated,
        keycloak.token,
        storedTenant,
        storedUserId,
        storedPrincipalTenant,
        storedPrincipalUserId,
        tokenRevision,
    ]);

    useEffect(() => {
        const transitionRevision = ++authTransitionRevisionRef.current;
        const isCurrentTransition = () => authTransitionRevisionRef.current === transitionRevision;

        if (derivedAuth.status === 'initializing') {
            setAuthStatus('initializing');
            return;
        }

        if (derivedAuth.status === 'unauthenticated') {
            setAuthStatus('unauthenticated');
            const hadMatrixSession = Boolean(
                activeContextKeyRef.current ||
                storedPrincipalTenant ||
                storedPrincipalUserId ||
                matrixService.getClient() ||
                matrixService.getState().isAuthenticated
            );
            activeContextKeyRef.current = null;
            if (hadMatrixSession) {
                void resetMatrixSession().catch((error) => {
                    logError('Failed to reset Matrix after authentication ended', {
                        source: 'auth',
                        event: 'matrix_auth_cleanup_failed',
                        error,
                    });
                });
            }
            dispatch(api.util.resetApiState());
            dispatch(clearAuthData());
            return;
        }

        if (derivedAuth.status === 'error') {
            setAuthStatus('error');
            const hadMatrixSession = Boolean(
                activeContextKeyRef.current ||
                matrixService.getClient() ||
                matrixService.getState().isAuthenticated
            );
            activeContextKeyRef.current = null;
            if (hadMatrixSession) {
                void resetMatrixSession().catch((cleanupError) => {
                    logError('Failed to reset Matrix after authentication error', {
                        source: 'auth',
                        event: 'matrix_auth_error_cleanup_failed',
                        error: cleanupError,
                    });
                });
            }
            dispatch(api.util.resetApiState());
            dispatch(clearAuthData());
            logError('Failed to build authenticated application context', {
                source: 'auth',
                event: 'context_build_failed',
                error: derivedAuth.error,
            });
            return;
        }

        const {context, contextKey} = derivedAuth;
        const mustResetMatrix = shouldResetMatrixForAccountChange({
            activeContextKey: activeContextKeyRef.current,
            cleanupFailed: matrixCleanupFailedRef.current,
            persistedPrincipal: {
                principalTenant: storedPrincipalTenant,
                principalUserId: storedPrincipalUserId,
            },
            nextContext: context,
        });

        const applyAuthenticatedContext = async () => {
            if (mustResetMatrix || matrixCleanupPendingRef.current) {
                setAuthStatus('initializing');
            }

            if (mustResetMatrix) {
                dispatch(api.util.resetApiState());
                await enforceMatrixAccountBoundary({
                    activeContextKey: activeContextKeyRef.current,
                    cleanupFailed: matrixCleanupFailedRef.current,
                    persistedPrincipal: {
                        principalTenant: storedPrincipalTenant,
                        principalUserId: storedPrincipalUserId,
                    },
                    nextContext: context,
                    resetMatrixSession,
                });
            } else if (matrixCleanupPendingRef.current) {
                await matrixCleanupPromiseRef.current;
            }

            if (!isCurrentTransition()) return;

            activeContextKeyRef.current = contextKey;
            dispatch(setAuthContext(context));
            logAuthSuccess(context.principalUserId, context.principalTenant, 'keycloak', sipInstanceId);
            setAuthStatus('ready');
        };

        void applyAuthenticatedContext().catch((error) => {
            if (!isCurrentTransition()) return;
            activeContextKeyRef.current = null;
            setAuthStatus('error');
            logError('Failed to establish account session boundary', {
                source: 'auth',
                event: 'account_session_boundary_failed',
                error,
            });
        });
    }, [
        derivedAuth,
        dispatch,
        resetMatrixSession,
        sipInstanceId,
        storedPrincipalTenant,
        storedPrincipalUserId,
    ]);

    useEffect(() => {
        const stopSip = () => {
            void sipUserAgent?.stop();
        };
        window.addEventListener("beforeunload", stopSip);
        return () => window.removeEventListener("beforeunload", stopSip);
    }, []);

    const matrixAccountBoundaryRequired = derivedAuth.status === 'ready' &&
        shouldResetMatrixForAccountChange({
            activeContextKey: activeContextKeyRef.current,
            cleanupFailed: matrixCleanupFailedRef.current,
            persistedPrincipal: {
                principalTenant: storedPrincipalTenant,
                principalUserId: storedPrincipalUserId,
            },
            nextContext: derivedAuth.context,
        });

    if (derivedAuth.status === 'error' || authStatus === 'error') {
        return <div>Authentication failed. Please reload the page.</div>;
    }

    if (
        derivedAuth.status === 'initializing' ||
        derivedAuth.status === 'unauthenticated' ||
        authStatus === 'initializing' ||
        authStatus === 'unauthenticated' ||
        matrixAccountBoundaryRequired
    ) {
        return <div className="h-screen flex justify-center items-center">
            <div
                className=" h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status">
                <span
                    className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
            </div>
        </div>;
    }

    // Auth is fully established and application context is in Redux. Keycloak
    // remains the sole owner of the access token.
    return <AuthedApp/>;
}

function AuthedApp() {

    const dispatch = useAppDispatch();

    const userId = useSelector((state: RootState) => state.auth.userId);
    const userRole = useSelector(selectUserRole);
    const isSuperadmin = useSelector(selectIsSuperadmin);
    const storedTenant = useSelector(selectTenant);
    const storedUserId = useSelector(selectUserId);
    const deviceId = useSelector(selectDeviceId);

    const {keycloak} = useKeycloak();

    const {data: isChatEnabled} = useIsChatEnabledQuery(undefined, {skip: !userId});
    const {data: isEnsEnabled} = useIsEnsEnabledQuery(undefined, {skip: !userId});
    const {data: profileSettings} = useGetUserProfileQuery(userId, {skip: !userId || isSuperadmin});
    const {data: currentUser} = useFetchCurrentUserQuery(userId, {skip: !userId || isSuperadmin});

    const accountBlocked = useAccountBlocked();
    const [login] = useLoginMutation();
    const [updateUserTimezone] = useUpdateUserTimezoneMutation();
    const [markChatInitialized] = useMarkChatInitializedMutation();
    const markInitializedRef = useRef(false);
    const initializationRef = useRef({
        attempted: false,
        inProgress: false
    });
    const matrixInitializationRevisionRef = useRef(0);
    const initInvocationRef = useRef(0);
    const matrixInitializationScopeRef = useRef({
        tenant: storedTenant,
        userId: storedUserId,
    });
    const isAuthedAppMountedRef = useRef(false);
    const [chatInitFailure, setChatInitFailure] = useState<ChatInitFailureState | null>(null);
    const chatInitFailureRef = useRef<ChatInitFailureState | null>(null);
    chatInitFailureRef.current = chatInitFailure;
    const initializeChatRef = useRef<() => Promise<ChatInitRunResult>>(async () => undefined);
    // Device a failed attempt logged in as, so retries reuse it rather than minting one per attempt.
    const chatInitAttemptsRef = useRef(new ChatInitAttemptTracker());
    const chatInitRetryRef = useRef<ChatInitRetryScheduler | null>(null);
    const softLogoutRecoveryRef = useRef<SoftLogoutRecovery | null>(null);
    if (chatInitRetryRef.current === null) {
        chatInitRetryRef.current = createChatInitRetryScheduler(() => {
            softLogoutRecoveryRef.current?.beforeScheduledRun();
            // inProgress is left alone: initializeChat refuses to run twice at once.
            initializationRef.current.attempted = false;
            return initializeChatRef.current();
        });
    }
    const cancelChatInitRetry = useCallback(() => {
        chatInitRetryRef.current?.cancel();
        chatInitAttemptsRef.current.reset();
        softLogoutRecoveryRef.current?.reset();
        setChatInitFailure(null);
    }, []);
    const activeMatrixLockScopeRef = useRef<MatrixSessionLockScope | null>(null);
    const {
        isAuthenticated,
        isInitialized: isChatInitialized,
        syncState,
        initialize,
        setSessionLockState,
        stopForSessionLockLoss,
    } = useMatrixContext();

    matrixInitializationScopeRef.current = {
        tenant: storedTenant,
        userId: storedUserId,
    };

    useEffect(() => {
        isAuthedAppMountedRef.current = true;
        return () => {
            isAuthedAppMountedRef.current = false;
            matrixInitializationRevisionRef.current += 1;
            chatInitRetryRef.current?.cancel();
        };
    }, []);

    useEffect(() => {
        if (isChatInitialized) cancelChatInitRetry();
    }, [isChatInitialized, cancelChatInitRetry]);

    useEffect(() => {
        if (!isChatInitialized || !userId || !currentUser) return;
        if (currentUser.chatInitializedAt != null) return;
        if (markInitializedRef.current) return;
        markInitializedRef.current = true;
        markChatInitialized({userId}).unwrap().catch(error => {
            markInitializedRef.current = false;
            logError('Failed to mark chat as initialized', {
                source: 'chat',
                event: 'mark_chat_initialized_failed',
                error,
            });
        });
    }, [isChatInitialized, userId, currentUser, markChatInitialized]);

    useEffect(() => {
        if (!isAuthenticated && !keycloak.token) {
            initializationRef.current = { attempted: false, inProgress: false };
            cancelChatInitRetry();
        }
    }, [isAuthenticated, keycloak.token, cancelChatInitRetry]);

    useEffect(() => {
        matrixInitializationRevisionRef.current += 1;
        initializationRef.current = { attempted: false, inProgress: false };
        cancelChatInitRetry();
    }, [storedTenant, storedUserId, cancelChatInitRetry]);

    useEffect(() => {
        if (userId && profileSettings && !profileSettings?.userTimeZone) {
            const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            updateUserTimezone({userId: userId, userTimeZone: browserTimeZone});
        }
    }, [profileSettings, updateUserTimezone, userId]);

    const releaseActiveMatrixLock = useCallback(() => {
        const scope = activeMatrixLockScopeRef.current;
        if (!scope) return;

        releaseMatrixSessionLock(scope);
        activeMatrixLockScopeRef.current = null;
    }, []);

    const handleMatrixLockLost = useCallback(async () => {
        matrixInitializationRevisionRef.current += 1;
        activeMatrixLockScopeRef.current = null;
        initializationRef.current = { attempted: false, inProgress: false };
        cancelChatInitRetry();
        await stopForSessionLockLoss();
    }, [stopForSessionLockLoss, cancelChatInitRetry]);

    const initializeChat = useCallback(async (): Promise<ChatInitRunResult> => {
        if (getAccountBlocked() ||
            initializationRef.current.attempted ||
            initializationRef.current.inProgress ||
            softLogoutRecoveryRef.current?.isGateOpen() === false ||
            isAuthenticated ||
            !keycloak.token ||
            !storedTenant ||
            isSuperadmin ||
            !storedUserId) {
            return 'blocked';
        }

        const initializationRevision = matrixInitializationRevisionRef.current;
        const initializationScope = {
            tenant: storedTenant,
            userId: storedUserId,
        };
        const isCurrentInitialization = () =>
            isAuthedAppMountedRef.current &&
            matrixInitializationRevisionRef.current === initializationRevision &&
            matrixInitializationScopeRef.current.tenant === initializationScope.tenant &&
            matrixInitializationScopeRef.current.userId === initializationScope.userId;

        if (!isCurrentInitialization()) return 'blocked';

        initInvocationRef.current += 1;
        const invocationId = initInvocationRef.current;
        initializationRef.current.inProgress = true;
        let stage = 'lock';
        let loggedInThisAttempt = false;

        try {
            const lockScope: MatrixSessionLockScope = {
                tenant: storedTenant,
                userId: storedUserId,
            };
            const lockState = acquireMatrixSessionLock(lockScope, {
                onLockLost: handleMatrixLockLost,
            });

            if (lockState === 'standby') {
                cancelChatInitRetry();
                if (shouldAutoClaimChatLock(lockScope, document.visibilityState)) {
                    setSessionLockState(requestMatrixSessionLockTakeover(lockScope) ? 'takeover_pending' : 'standby');
                    return;
                }
                setSessionLockState(hasPendingMatrixSessionLockClaim(lockScope) ? 'takeover_pending' : 'standby');
                return;
            }

            markChatLockAutoClaimSpent(lockScope);

            activeMatrixLockScopeRef.current = lockScope;
            initializationRef.current.attempted = true;
            setSessionLockState('active');

            // A null id makes the backend mint a new device on every login.
            let loginDeviceId = chatInitAttemptsRef.current.nextLoginDeviceId(storedTenant, storedUserId, deviceId, getStoredDeviceId(storedTenant, storedUserId));
            stage = 'matrix_init';
            const persistedSession = readPersistedMatrixSession(lockScope);
            if (persistedSession.accessToken && persistedSession.refreshToken && persistedSession.expiresAtMs !== null) {
                try {
                    const expiresInMs = Math.max(0, persistedSession.expiresAtMs - Date.now());
                    await initialize({
                        accessToken: persistedSession.accessToken,
                        refreshToken: persistedSession.refreshToken,
                        expiresInMs,
                    });
                    if (!isCurrentInitialization()) return;
                    dispatch(initializeDeviceId({ tenant: storedTenant, userId: storedUserId }));
                    return;
                } catch (error) {
                    if (!isCurrentInitialization()) return;
                    if (error instanceof MatrixDeviceIdMismatchError) {
                        loginDeviceId = error.storedDeviceId;
                    } else if (error instanceof MatrixCryptoStoreEvictedError) {
                        loginDeviceId = null;
                    }
                    dispatch(setMatrixSession({
                        accessToken: null,
                        refreshToken: null,
                        expiresAtMs: null,
                    }));
                }
            }

            let authResponse: ChatLoginResponse | undefined;

            for (let deviceLoginAttempt = 1; deviceLoginAttempt <= 3; deviceLoginAttempt += 1) {
                stage = 'login';
                const loginResponse = await login({deviceId: loginDeviceId}).unwrap();
                if (!isCurrentInitialization()) return;
                stage = 'matrix_init';
                loggedInThisAttempt = true;

                logDev('Chat authentication successful', {
                    source: 'chat',
                    event: 'auth_success',
                    deviceId: loginDeviceId,
                    tenant: storedTenant
                });

                try {
                    await initialize(loginResponse);
                    authResponse = loginResponse;
                    break;
                } catch (error) {
                    if (!isCurrentInitialization()) return;
                    if (error instanceof MatrixDeviceIdMismatchError) {
                        if (loginDeviceId === error.storedDeviceId) {
                            throw error;
                        }
                        logDev('Recovering the Matrix device associated with the preserved encryption store', {
                            source: 'encryption',
                            event: 'matrix_device_id_recovery_retry',
                            receivedDeviceId: error.receivedDeviceId,
                            storedDeviceId: error.storedDeviceId,
                            tenant: storedTenant,
                        });
                        loginDeviceId = error.storedDeviceId;
                        chatInitAttemptsRef.current.setDevice(storedTenant, storedUserId, error.storedDeviceId);
                        continue;
                    }

                    if (error instanceof MatrixCryptoStoreEvictedError && loginDeviceId !== null) {
                        logDev('Encryption storage was evicted; retrying with a new Matrix device', {
                            source: 'encryption',
                            event: 'crypto_storage_new_device_retry',
                            previousDeviceId: loginDeviceId,
                            tenant: storedTenant,
                        });
                        loginDeviceId = null;
                        chatInitAttemptsRef.current.setDevice(storedTenant, storedUserId, null);
                        continue;
                    }

                    throw error;
                }
            }
            if (!authResponse) {
                throw new Error('Matrix device recovery exceeded the retry limit');
            }
            if (!isCurrentInitialization()) return;
            const cleanToken = authResponse.accessToken.startsWith('Bearer ')
                ? authResponse.accessToken.substring(7)
                : authResponse.accessToken;
            dispatch(setMatrixSession({
                accessToken: cleanToken,
                refreshToken: authResponse.refreshToken ?? null,
                expiresAtMs: typeof authResponse.expiresInMs === 'number'
                    ? Date.now() + authResponse.expiresInMs
                    : null,
            }));
            dispatch(initializeDeviceId({ tenant: storedTenant, userId: storedUserId }));
        } catch (error) {
            if (!isCurrentInitialization()) return 'blocked';
            const failedStage = stage === 'matrix_init'
                ? `matrix_init:${matrixService.getInitStage()}`
                : stage;
            const createdDeviceId = loggedInThisAttempt ? matrixService.getLastInitDeviceId(storedTenant, storedUserId) : null;
            chatInitAttemptsRef.current.recordFailure({tenant: storedTenant, userId: storedUserId, loggedIn: loggedInThisAttempt, createdDeviceId});
            const retriesExhausted = chatInitAttemptsRef.current.exhausted;
            const blockedByError = recordAccountBlock(error);
            const classifiedPermanent = blockedByError || isPermanentChatInitError(error);
            const permanent = classifiedPermanent || retriesExhausted;
            let retryDelayMs: number | null = null;
            if (permanent) {
                chatInitRetryRef.current?.cancel();
            } else {
                retryDelayMs = chatInitRetryRef.current?.scheduleAfterFailure() ?? null;
            }
            logError('Chat initialization failed', {
                source: 'chat',
                event: 'init_failed',
                error: error,
                errorMessage: error instanceof Error ? error.message : undefined,
                stage: failedStage,
                permanent,
                retriesExhausted,
                postLoginFailures: chatInitAttemptsRef.current.failuresAfterLogin,
                retryDelayMs,
                deviceId,
                tenant: storedTenant
            });
            setChatInitFailure({
                permanent,
                reason: classifiedPermanent ? 'classified' : retriesExhausted ? 'retries_exhausted' : undefined,
            });
            if (!blockedByError) {
                initializationRef.current.attempted = false;
            }
        } finally {
            if (initInvocationRef.current === invocationId) {
                initializationRef.current.inProgress = false;
            }
        }
    }, [deviceId, handleMatrixLockLost, cancelChatInitRetry, isAuthenticated, initialize, isSuperadmin, keycloak.token, login, setSessionLockState, storedTenant, storedUserId, dispatch]);

    initializeChatRef.current = initializeChat;

    const handleChatRetryNow = useCallback(() => {
        if (chatInitFailure) {
            softLogoutRecoveryRef.current?.onManualRetry();
            chatInitRetryRef.current?.retryNow();
        } else {
            matrixService.getClient()?.retryImmediately();
        }
    }, [chatInitFailure]);

    if (softLogoutRecoveryRef.current === null) {
        softLogoutRecoveryRef.current = createSoftLogoutRecovery({
            tracker: chatInitAttemptsRef.current,
            schedule: minFailureCount => chatInitRetryRef.current?.scheduleAfterFailure(minFailureCount) ?? 0,
            cancel: () => chatInitRetryRef.current?.cancel(),
            setFailure: setChatInitFailure,
            releaseLock: releaseActiveMatrixLock,
            bumpRevision: () => {
                matrixInitializationRevisionRef.current += 1;
            },
            resetInitState: () => {
                initializationRef.current.attempted = false;
            },
        });
    }

    const handleAuthenticationExpired = useCallback(() => {
        if (!isAuthedAppMountedRef.current || !storedTenant || !storedUserId || isChatEnabled === false) return;

        const outcome = softLogoutRecoveryRef.current?.onAuthenticationExpired();
        logDev('Chat session expired; re-initializing chat', {
            source: 'chat',
            event: 'auth_expired_reinit',
            softLogouts: outcome?.softLogouts,
            exhausted: outcome?.exhausted,
            retryDelayMs: outcome?.retryDelayMs,
            tenant: storedTenant,
        });
    }, [isChatEnabled, storedTenant, storedUserId]);

    useEffect(() => {
        matrixService.on('authenticationExpired', handleAuthenticationExpired);
        return () => {
            matrixService.off('authenticationExpired', handleAuthenticationExpired);
        };
    }, [handleAuthenticationExpired]);

    useEffect(() => {
        return attachChatInitAutoRetry({
            canRetry: () => isAutoRetryableChatInitFailure(chatInitFailureRef.current),
            retry: () => chatInitRetryRef.current?.retryNow(),
        });
    }, []);

    const handleSessionTakeoverRequested = useCallback(() => {
        if (!storedTenant || !storedUserId || isChatEnabled === false) {
            setSessionLockState('standby');
            return;
        }

        initializationRef.current = { attempted: false, inProgress: false };
        const requested = requestMatrixSessionLockTakeover({
            tenant: storedTenant,
            userId: storedUserId,
        });
        if (!requested) {
            setSessionLockState('standby');
        }
    }, [isChatEnabled, setSessionLockState, storedTenant, storedUserId]);

    useEffect(() => {
        matrixService.on('sessionTakeoverRequested', handleSessionTakeoverRequested);
        return () => {
            matrixService.off('sessionTakeoverRequested', handleSessionTakeoverRequested);
        };
    }, [handleSessionTakeoverRequested]);

    useEffect(() => {
        if(!isSuperadmin && isChatEnabled !== false) {
            initializeChat();
        } else {
            cancelChatInitRetry();
            releaseActiveMatrixLock();
            if (storedTenant && storedUserId) {
                cancelMatrixSessionLockTakeover({tenant: storedTenant, userId: storedUserId});
            }
        }
    }, [initializeChat, isChatEnabled, isSuperadmin, releaseActiveMatrixLock, cancelChatInitRetry, storedTenant, storedUserId]);

    useEffect(() => {
        if (!storedTenant || !storedUserId || isChatEnabled === false) return;

        const scope: MatrixSessionLockScope = {
            tenant: storedTenant,
            userId: storedUserId,
        };

        let hadPendingClaim = false;
        return subscribeToMatrixSessionLock(scope, (snapshot) => {
            if (snapshot.state !== 'active') {
                if (hadPendingClaim && !snapshot.claimPending) {
                    setSessionLockState('standby');
                }
                hadPendingClaim = snapshot.claimPending;
                return;
            }
            hadPendingClaim = false;
            if (activeMatrixLockScopeRef.current) return;
            if (initializationRef.current.inProgress || isAuthenticated) return;

            initializationRef.current.attempted = false;
            void initializeChat();
        });
    }, [initializeChat, isAuthenticated, isChatEnabled, setSessionLockState, storedTenant, storedUserId]);

    useEffect(() => {
        if (!storedTenant || !storedUserId) return;

        const scope: MatrixSessionLockScope = {
            tenant: storedTenant,
            userId: storedUserId,
        };
        return () => cancelMatrixSessionLockTakeover(scope);
    }, [storedTenant, storedUserId]);

    useEffect(() => {
        return () => {
            releaseActiveMatrixLock();
        };
    }, [releaseActiveMatrixLock]);

    useEffect(() => {
        if (!accountBlocked) return;
        matrixInitializationRevisionRef.current += 1;
        void stopSipForBlockedAccount(dispatch);
        void stopForSessionLockLoss().finally(releaseActiveMatrixLock);
    }, [accountBlocked, dispatch, releaseActiveMatrixLock, stopForSessionLockLoss]);

    if (accountBlocked) {
        return (
            <div className="overflow-hidden">
                <AccountBlockedNotice reason={accountBlocked}/>
            </div>
        );
    }

    return (
        <>
            <div className="overflow-hidden">
                <StatusProvider>
                    <React.StrictMode>
                        <div className="flex flex-col min-h-screen overflow-hidden">
                            <SuperAdminBanner/>
                            <OnlineStatusDetector/>
                            <ChatOfflineIndicator
                                initFailed={chatInitFailure !== null}
                                permanentFailure={chatInitFailure?.permanent}
                                isChatInitialized={isChatInitialized}
                                syncState={syncState}
                                onRetry={handleChatRetryNow}
                            />
                            <BrowserRouter>
                            <Header/>
                            <ChSidebar
                                logo={logo}
                                sections={translateSections({isChatEnabled: isChatEnabled !== false, isEnsEnabled: isEnsEnabled === true, isAdmin: userRole === 'ADMIN'})}
                                isEnsEnabled={isEnsEnabled === true}
                            />
                            <MainContentWrapper
                                className="flex flex-1"
                            >
                                <div className='w-full flex-grow relative '>
                                    <Routes>
                                        <Route path="/calls" element={
                                            <UserRoute>
                                                <Calls/>
                                            </UserRoute>
                                        }/>
                                        <Route path="/transcript/:id" element={
                                            <UserRoute>
                                                <CallTranscript/>
                                            </UserRoute>
                                        }/>
                                        {isChatEnabled !== false && <Route path="/chat" element={
                                            <UserRoute>
                                                <Chat/>
                                            </UserRoute>
                                        }/>}
                                        <Route path="/contacts" element={
                                            <UserRoute>
                                                <ContactsList/>
                                            </UserRoute>
                                        }/>
                                        <Route path="/contacts/:id" element={
                                            <UserRoute>
                                                <ContactDetails/>
                                            </UserRoute>
                                        }/>
                                        <Route path="/contacts/new" element={
                                            <Navigate to="/contacts" replace/>
                                        }/>
                                        <Route path="/contacts/details/:id" element={
                                            <UserRoute>
                                                <ContactDetails/>
                                            </UserRoute>
                                        }/>
                                        <Route path="/team" element={
                                            <UserRoute>
                                                <TeamList/>
                                            </UserRoute>
                                        }/>
                                        <Route path="/groups" element={
                                            <UserRoute>
                                                <SettingsLayout title="Groups"><GroupList admin={false}/></SettingsLayout>
                                            </UserRoute>
                                        }/>
                                        <Route path="/groups/details/:id" element={
                                            <UserRoute>
                                                <SettingsLayout title="Group" back={{label: "Groups", to: "/groups"}}><GroupDetails admin={false}/></SettingsLayout>
                                            </UserRoute>
                                        }/>
                                        <Route path="/groups/details/new" element={
                                            <UserRoute>
                                                <SettingsLayout title="New group" back={{label: "Groups", to: "/groups"}}><GroupDetails admin={false}/></SettingsLayout>
                                            </UserRoute>
                                        }/>
                                        <Route path="/phoneNumbers" element={
                                            <UserRoute>
                                                <SettingsLayout title="Phone numbers"><PhoneNumbers/></SettingsLayout>
                                            </UserRoute>
                                        }/>
                                        <Route path="/phoneNumbers/:id" element={
                                            <UserRoute>
                                                <SettingsLayout title="Phone numbers"><PhoneNumbers/><PhoneNumberSettings admin={false}/></SettingsLayout>
                                            </UserRoute>
                                        }/>
                                        <Route path="/settings" element={<UserRoute><GeneralSettings/></UserRoute>}/>
                                        <Route path="/settings/audio" element={<UserRoute><AudioSettingsPage/></UserRoute>}/>
                                        <Route path="/settings/permissions" element={<UserRoute><PermissionsSettings/></UserRoute>}/>
                                        <Route path="/profile" element={
                                            isSuperadmin ? <Navigate to="/" replace/> : (
                                                <UserRoute>
                                                    <SettingsLayout title="Profile"><ProfileSettings/></SettingsLayout>
                                                </UserRoute>
                                            )
                                        }/>
                                        <Route path="/recordings/:id" element={
                                            <UserRoute>
                                                <RecordingDownload/>
                                            </UserRoute>
                                        }/>

                                        <Route path="/admin/integrations" element={
                                            <AdminRoute>
                                                <AdminSettingsFrame><Integrations/></AdminSettingsFrame>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/admin/integrations/:id" element={
                                            <AdminRoute>
                                                <AdminSettingsFrame><Integration/></AdminSettingsFrame>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/admin/integrations/:id/:step" element={
                                            <AdminRoute>
                                                <AdminSettingsFrame><Integration/></AdminSettingsFrame>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/admin/report" element={
                                            <AdminRoute>
                                                <AdminSettingsFrame><BillingReport/></AdminSettingsFrame>
                                            </AdminRoute>
                                        }/>
                                        {/* Call queues moved from the admin panel to Settings › Calls. */}
                                        <Route path="/admin/callQueues" element={<Navigate to="/callQueues" replace/>}/>
                                        <Route path="/admin/callQueues/:id" element={<AdminQueueRedirect/>}/>
                                        <Route path="/callQueues" element={
                                            <AdminRoute>
                                                <CallQueuesPage/>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/callQueues/:id" element={
                                            <AdminRoute>
                                                <CallQueuesPage/>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/admin/phoneNumbers" element={
                                            <AdminRoute>
                                                <AdminSettingsFrame><PhoneNumbers admin={true}/></AdminSettingsFrame>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/admin/phoneNumbers/:id" element={
                                            <AdminRoute>
                                                <AdminSettingsFrame><><PhoneNumbers admin={true}/><PhoneNumberSettings admin={true}/></></AdminSettingsFrame>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/admin/groups" element={
                                            <AdminRoute>
                                                <AdminSettingsFrame><GroupList admin={true}/></AdminSettingsFrame>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/admin/groups/details/:id" element={
                                            <AdminRoute>
                                                <AdminSettingsFrame><GroupDetails admin={true}/></AdminSettingsFrame>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/admin/groups/details/new" element={
                                            <AdminRoute>
                                                <AdminSettingsFrame><GroupDetails admin={true}/></AdminSettingsFrame>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/admin/team" element={
                                            <AdminRoute>
                                                <AdminSettingsFrame><TeamListAdmin/></AdminSettingsFrame>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/admin/team-details/:id" element={
                                            <AdminRoute>
                                                <AdminSettingsFrame><TeamDetailsAdmin admin={true}/></AdminSettingsFrame>
                                            </AdminRoute>
                                        }/>
                                        <Route path="/admin/integrations/chat-webhooks" element={<AdminRoute><AdminSettingsFrame><IncomingChatWebhooks/></AdminSettingsFrame></AdminRoute>}/>
                                        <Route
                                            path="/admin/webhook/:id/deliveries"
                                            element={
                                                <AdminRoute>
                                                    <AdminSettingsFrame><WebhookDeliveries/></AdminSettingsFrame>
                                                </AdminRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/webhook/:id"
                                            element={
                                                <AdminRoute>
                                                    <AdminSettingsFrame><WebhookList/></AdminSettingsFrame>
                                                </AdminRoute>
                                            }
                                        />
                                         <Route
                                            path="/admin/api/list"
                                            element={
                                                <AdminRoute>
                                                    <AdminSettingsFrame><ApiList/></AdminSettingsFrame>
                                                </AdminRoute>
                                            }
                                        />
                                        {isEnsEnabled === true && <Route path="/admin/ens" element={
                                            <AdminRoute>
                                                <EnsMain/>
                                            </AdminRoute>
                                        }/>}
                                        {isEnsEnabled === true && <Route path="/admin/ens/alerts/:publicId" element={
                                            <AdminRoute>
                                                <EnsMain/>
                                            </AdminRoute>
                                        }/>}
                                        {isEnsEnabled === true && <Route path="/admin/ens/contacts" element={
                                            <AdminRoute>
                                                <EnsUserContactsAdmin/>
                                            </AdminRoute>
                                        }/>}
                                        {isEnsEnabled === true && <Route path="/ens/my-alerts" element={
                                            <UserRoute>
                                                <MyAlerts/>
                                            </UserRoute>
                                        }/>}

                                        <Route path="/welcome" element={
                                            <UserRoute>
                                                <HomePage/>
                                            </UserRoute>
                                        }/>
                                        <Route path="*"
                                               element={userRole == 'SUPERADMIN' ? <Navigate to="/admin/report" replace/> : <HomePage/>}/>
                                    </Routes>
                                </div>
                            </MainContentWrapper>
                            <ToastNotification/>
                            <EnsNotificationPoller enabled={isEnsEnabled === true}/>
                            <CallChangesPoller enabled={!isSuperadmin}/>
                        </BrowserRouter>
                        {!isSuperadmin && <CallSurfaceRoot matrixEnabled={isChatEnabled !== false}/>}
                    </div>
                </React.StrictMode>
                </StatusProvider>
            </div>
        </>
    );
}

function MainContentWrapper({
                                children,
                                className,
                            }: {
    children: React.ReactNode;
    className: string;
}) {
    const location = useLocation();

    const isCallsPath = location.pathname === '/calls';
    const isTranscriptPath = location.pathname.includes('/transcript');

    return (
        <div
            className={`${className} ${(isCallsPath || isTranscriptPath) ? 'ml-[300px]' : 'ml-nav-rail'}`}>
            {children}
        </div>
    );

}

const AdminQueueRedirect = () => {
    const {id} = useParams<{ id: string }>();
    return <Navigate to={`/callQueues/${id ?? ''}`} replace/>;
};

const AdminRoute = ({children}: { children: React.ReactNode; }) => {
    const userRole = useSelector(selectUserRole);
    if (userRole !== 'ADMIN' && userRole !== 'SUPERADMIN') {
        return <AdminPanelRestrictedView/>;
    }

    return children;
};

const UserRoute = ({children}: { children: React.ReactNode; }) => {
    const userRole = useSelector(selectUserRole);

    if (userRole === 'SUPERADMIN') {
        return <RestrictedView/>;
    }

    return children;
};


export default App;
